import { CRAWLERS } from "../../src/app.config.js";
import _ from "underscore";
import errorText from "../../src/i18n/en/client-side-validation-errors.json";

const VALID_NORMAL_SEQUENCE = /[ACGTRYKMSWBDHVNXacgtrykmswbdhvnx0-9\s]/,
  VARIANT_REGION_GROUPING = /^([\{\[]){1}.+([\}\]])$/,
  VALID_COSMIC_ID = /^COS(M|V)([0-9]+)$/,
  VALID_GENE_SYMBOL = /^[A-Za-z][0-9A-Za-z]+$/,
  VALID_CND_CHROMOSOMAL_LOCATION = /^chr([A-Za-z0-9]{1,2}):([0-9,]+)-([0-9,]+)$/,
  WHITESPACE = /\s+/g,
  MULTI_WORD_MUTATION_IDENTIFIER = /^[A-Za-z][0-9A-Za-z]+\s+([^\s]+\s*){1,2}$/,
  MULTI_WORD_GENE_EXPRESSION = /^[A-Za-z][0-9A-Za-z]+\s+([^\s]+\s*){1,2}$/,
  DBSNP_ID = /^(rs[0-9]+)$/,
  INVALID_NUCLEOTIDE = /[^ACGTNXRYKMSWBDHVacgtnxrykmswbdhv]/,
  INVALID_NUCLEOTIDES = new RegExp(INVALID_NUCLEOTIDE.source, "g"),
  INVALID_UNAMBIGUOUS_NUCLEOTIDE = /[^ACGTacgt]/,
  DIGIT = /[0-9]/,
  WHITESPACE_CHAR = /\s/,
  GENOMES = [
    { build: "hg19", organism: "Homo_sapiens" },
    { build: "mm10", organism: "Mus_musculus" },
    { build: "rn5", organism: "Rattus_norvegicus" },
    { build: "Other" },
  ],
  SEQUENCE_CONFIG = {
    mutation: { minSequenceFlankLength: 61, maxSequenceLength: 1000 },
    cnd: { minSequenceLength: 123, maxSequenceLength: 1000 },
    cellAndGeneTherapy: { minSequenceLength: 123, maxSequenceLength: 1500 },
    genomeEdit: {
      donor: { minSequenceLength: 50, maxSequenceLength: 10000 },
      context: {
        minBufferLength: 25,
        minSequenceLength: 50,
        maxSequenceLength: 10000,
        withDonorMinBufferLength: 50,
        withDonorAdditionalMinSequenceLength: 100,
        genomeOtherMinBufferLength: 100,
        genomeOtherMinSequenceLength: 200,
      },
    },
  };

export const requiredVectorBackbone = (value) =>
  value ? undefined : "Please select an option.";

export const required = (value) =>
  value ? undefined : "No identifier was provided";
export const maxLength = (max) => (value) =>
  value && value.length > max ? `Must be ${max} characters or less` : undefined;
export const maxLength17 = maxLength(17);
export const requiredSeq = (value) =>
  value
    ? undefined
    : "There was no sequence provided. Please provide a sequence";
export const requiredSeqCellAndGene = (value) =>
  value
    ? undefined
    : "There was no sequence provided. Please provide a sequence or click the Remove button to order the Vector Backbone Assay only";
export const requiredTarget = (value) =>
  value
    ? undefined
    : "Target Name field cannot be empty. Enter a valid Target Name";
export const minLength = (min) => (value) =>
  value && value.length < min ? `Must be ${min} characters or more` : undefined;

export const minLength2 = minLength(2);
export const minLength5 = minLength(5);
export const minLength12 = minLength(12);

export const hasValidWhitespace = (value) => {
  const validWs = !value || !/[\t\r\n\f]+/.test(value);
  return !validWs ? `Invalid white space. ` : undefined;
};

const isValidCosmicId = (value) =>
  !value ||
  _.isEmpty(value) ||
  VALID_COSMIC_ID.test(!_.isEmpty(value) && value.trim().toUpperCase());

const isValidMultiWordMutationIdentifier = (value) =>
  !value ||
  _.isEmpty(value) ||
  MULTI_WORD_MUTATION_IDENTIFIER.test(_.isString(value) && value.trim());

const isValidMultiWordGeneExpression = (value) =>
  !value ||
  _.isEmpty(value) ||
  MULTI_WORD_GENE_EXPRESSION.test(_.isString(value) && value.trim());

const containsDbSNPId = (identifier) => {
  if (!_.isString(identifier)) return false;
  var components = identifier.split(WHITESPACE);
  for (var i = 0; i < components.length; i++) {
    if (DBSNP_ID.test(components[i])) {
      return true;
    }
  }
  return false;
};

const isValidCndGeneSymbol = (value) =>
  !value ||
  _.isEmpty(value) ||
  VALID_GENE_SYMBOL.test(_.isString(value) && value.trim());

const isValidCndChromosomalLocation = (value) =>
  !value ||
  _.isEmpty(value) ||
  VALID_CND_CHROMOSOMAL_LOCATION.test(_.isString(value) && value.trim());

const tokenizeVariantSequence = (sequenceStr) => {
  var tokens = [];
  var stateStartIndex = 0,
    stateEndIndex = 0;
  var currentState = "context";

  if (
    sequenceStr === undefined ||
    sequenceStr === null ||
    sequenceStr === [] ||
    sequenceStr === {}
  ) {
    throw { code: "noSequenceProvided" };
  }

  // attempt to tokenize sequence into objects based on context and variant region sections
  for (var i = 0; i < sequenceStr.length; i++) {
    var currentChar = sequenceStr.charAt(i);

    if (currentChar === "[") {
      if (currentState === "variantRegion") {
        throw { code: "unexpectedVariantRegionInitiator", position: i };
      }
      if (stateStartIndex !== stateEndIndex) {
        tokens.push({
          type: currentState,
          seq: sequenceStr.substring(stateStartIndex, stateEndIndex),
          start: stateStartIndex,
          end: stateEndIndex,
        });
      }
      currentState = "variantRegion";
      stateStartIndex = i;
      stateEndIndex = i + 1;
    } else if (currentChar === "]") {
      if (currentState === "context") {
        throw { code: "unexpectedVariantRegionTerminator", position: i };
      }
      stateEndIndex = i + 1;
      if (stateStartIndex !== stateEndIndex) {
        tokens.push({
          type: currentState,
          seq: sequenceStr.substring(stateStartIndex, stateEndIndex),
          start: stateStartIndex,
          end: stateEndIndex,
        });
      }
      currentState = "context";
      stateStartIndex = i + 1;
    } else {
      stateEndIndex = i + 1;
    }
  }

  if (currentState !== "context") {
    throw { code: "unterminatedVariantRegion", position: i };
  }

  if (stateStartIndex !== stateEndIndex) {
    tokens.push({
      type: currentState,
      seq: sequenceStr.substring(stateStartIndex, stateEndIndex),
      start: stateStartIndex,
      end: stateEndIndex,
    });
  }

  return tokens;
};

const validateNormalSequenceToken = (token) => {
  var errors = [];
  var sequence = token.seq;
  for (var i = 0; i < sequence.length; i++) {
    if (!VALID_NORMAL_SEQUENCE.test(sequence.charAt(i))) {
      errors.push({
        code: "invalidNucleotide",
        character: sequence.charAt(i),
        position: i + token.start,
      });
    }
  }
  return errors;
};

export const validateVariantRegionToken = (token) => {
  var sequence = token.seq,
    errors = [],
    nucleotideSeen = false,
    delimiterSeen = false,
    gapSeen = false,
    gapSeenThisSeq = false,
    char = "",
    position = 0;

  // first make sure that first and last chars of variant region are present
  if (!VARIANT_REGION_GROUPING.test(sequence)) {
    return [{ code: "invalidVariantRegionGrouping" }];
  }

  for (var i = 1; i < sequence.length - 1; i++) {
    char = sequence.charAt(i);
    position = i + token.start;

    if (WHITESPACE_CHAR.test(char) || DIGIT.test(char)) {
      continue;
    } else if (char === "/") {
      if (delimiterSeen) {
        errors.push({
          code: "unexpectedVariantDelimiter",
          position: position,
        });
      } else {
        if (!gapSeen && !nucleotideSeen) {
          errors.push({
            code: "nativeSequenceMissing",
            position: position,
          });
        }
        delimiterSeen = true;
        nucleotideSeen = false;
        gapSeenThisSeq = false;
      }
    } else if (char === "-") {
      if (gapSeen || nucleotideSeen) {
        errors.push({ code: "unexpectedGapCharacter", position: position });
      } else {
        gapSeen = true;
      }
      gapSeenThisSeq = true;
    } else if (INVALID_UNAMBIGUOUS_NUCLEOTIDE.test(char)) {
      errors.push({
        code: "invalidUnambiguousNucleotide",
        character: char,
        position: position,
      });
    } else {
      if (gapSeenThisSeq && !nucleotideSeen) {
        errors.push({ code: "unexpectedNucleotide", position: position });
      } else {
        nucleotideSeen = true;
      }
    }
  }
  if (!delimiterSeen) {
    errors.push({
      code: "variantDelimiterMissing",
      position: position + 1,
    });
  } else if (!gapSeenThisSeq && !nucleotideSeen) {
    errors.push({ code: "variantSequenceMissing", position: position + 1 });
  }
  return errors;
};

const hasExpectedMutationSequencePattern = (tokens) => {
  var errors = [],
    variantRegionSeen = false;
  for (var i = 0; i < tokens.length; i++) {
    var token = tokens[i];
    if (token.type === "variantRegion") {
      if (variantRegionSeen) {
        errors.push({ code: "tooManyVariantRegions" });
      }
      if (i === 0) {
        errors.push({ code: "noUpstreamContext" });
      }
      if (i === tokens.length - 1) {
        errors.push({ code: "noDownstreamContext" });
      }
      variantRegionSeen = true;
    }
  }
  if (!variantRegionSeen) {
    errors.push({ code: "noVariantRegionFound" });
  }
  return errors;
};

const toNucleotidesOnly = (sequence) =>
  (sequence && sequence.replace(INVALID_NUCLEOTIDES, "")) || "";

const countNucleotideCharacters = (sequence) =>
  toNucleotidesOnly(sequence).length;

const contextSequencesHaveMinimumLength = (
  contextTokens,
  minSequenceFlankLength
) => {
  var errors = [];
  contextTokens.forEach(function (token) {
    var seqLength = countNucleotideCharacters(token.seq);
    if (seqLength < minSequenceFlankLength) {
      errors.push({
        code: "contextBelowMinLength",
        location: token.location,
        length: seqLength,
        requiredLength: minSequenceFlankLength,
      });
    }
  });
  return errors;
};

const calcTotalSequenceLength = (sequences) => {
  var sequenceLengths = sequences.map(function (seq) {
    return countNucleotideCharacters(seq);
  });
  return sequenceLengths.reduce(function (length1, length2) {
    return length1 + length2;
  });
};

const contextSequenceWithinMaximumLength = (tokens, maxLength) => {
  var errors = [];
  var contextSequences = [tokens[0].seq, tokens[2].seq];
  var contextLength = calcTotalSequenceLength(contextSequences);
  if (contextLength > maxLength) {
    errors.push({
      code: "contextExceedsMaxLength",
      length: contextLength,
      requiredLength: maxLength,
    });
  }
  return errors;
};

function validateContextSequence(
  sequence,
  minBufferLength,
  minSequenceLength,
  maxSequenceLength,
  cutsiteRequired
) {
  if (sequence === undefined || sequence === null || sequence === "") {
    return [{ code: "noSequenceProvided" }];
  }
  var errors = [],
    char = "",
    upstreamNucleotideCount = 0,
    downstreamNucleotideCount = 0,
    totalNucleotideCount = 0,
    cutsiteIndicatorSeen = false;
  for (var i = 0; i < sequence.length; i++) {
    char = sequence.charAt(i);
    if (WHITESPACE_CHAR.test(char) || DIGIT.test(char)) {
      continue;
    }
    if (char === "^") {
      if (cutsiteIndicatorSeen) {
        errors.push({ code: "unexpectedCutsiteIndicator", position: i });
      }
      cutsiteIndicatorSeen = true;
    } else if (INVALID_NUCLEOTIDE.test(char)) {
      errors.push({ code: "invalidNucleotide", character: char, position: i });
    } else {
      if (!cutsiteIndicatorSeen) {
        upstreamNucleotideCount++;
      } else {
        downstreamNucleotideCount++;
      }
    }
  }
  totalNucleotideCount = upstreamNucleotideCount + downstreamNucleotideCount;
  if (cutsiteRequired) {
    if (cutsiteIndicatorSeen) {
      if (upstreamNucleotideCount < minBufferLength) {
        errors.push({
          code: "insufficientBufferSequence",
          side: "left",
          length: upstreamNucleotideCount,
          requiredLength: minBufferLength,
        });
      }
      if (downstreamNucleotideCount < minBufferLength) {
        errors.push({
          code: "insufficientBufferSequence",
          side: "right",
          length: downstreamNucleotideCount,
          requiredLength: minBufferLength,
        });
      }
    } else {
      errors.push({ code: "cutsiteIndicatorMissing" });
    }
  }
  if (totalNucleotideCount < minSequenceLength) {
    errors.push({
      code: "contextBelowMinLength",
      length: totalNucleotideCount,
      requiredLength: minSequenceLength,
    });
  }
  if (totalNucleotideCount > maxSequenceLength) {
    errors.push({
      code: "contextExceedsMaxLength",
      length: totalNucleotideCount,
      requiredLength: maxSequenceLength,
    });
  }

  return errors;
}

const _validateMutationSequence = (
  sequence,
  minContextLength,
  maxSequenceLength
) => {
  var errors = [];

  // try to tokenize the sequence in order to identify problems
  var tokens;
  try {
    tokens = tokenizeVariantSequence(sequence);
  } catch (e) {
    var error = _.pick(e, "code", "position");
    return [error];
  }

  if (tokens.length === 0) {
    return [{ code: "noSequenceProvided" }];
  }

  var tokenPatternErrors = hasExpectedMutationSequencePattern(tokens);

  if (tokenPatternErrors.length > 0) {
    return tokenPatternErrors;
  }

  var upstreamContextSeq = tokens[0],
    downstreamContextSeq = tokens[2];

  upstreamContextSeq.location = "left";
  downstreamContextSeq.location = "right";

  errors = errors.concat(
    contextSequencesHaveMinimumLength(
      [upstreamContextSeq, downstreamContextSeq],
      minContextLength
    )
  );
  errors = errors.concat(
    contextSequenceWithinMaximumLength(tokens, maxSequenceLength)
  );

  tokens.forEach((token) => {
    switch (token.type) {
      case "context":
        errors = errors.concat(validateNormalSequenceToken(token));
        break;
      case "variantRegion":
        errors = errors.concat(validateVariantRegionToken(token));
        break;
    }
  });
  return errors;
};

export const validateMutationIdentifier = (identifier) => {
  var errors = [];
  var invalidIdentifier = { code: "invalidMutationIdentifier" };
  if (identifier === null || identifier === undefined || identifier === "") {
    errors = [{ code: "noIdentifierProvided" }];
  }
  if (isValidCosmicId(identifier)) {
    return undefined;
  } else if (containsDbSNPId(identifier)) {
    errors = [invalidIdentifier];
  } else if (!isValidMultiWordMutationIdentifier(identifier)) {
    errors = [invalidIdentifier];
  }
  return _handleErrors("mutation", errors);
};

export const validateGeneExpressionIdentifier = (identifier) => {
  var errors = [];
  var invalidIdentifier = { code: "invalidMutationIdentifier" };
  if (identifier === null || identifier === undefined || identifier === "") {
    errors = [{ code: "noIdentifierProvided" }];
  }
  if (isValidCosmicId(identifier)) {
    return undefined;
  } else if (containsDbSNPId(identifier)) {
    errors = [invalidIdentifier];
  } else if (!isValidMultiWordGeneExpression(identifier)) {
    errors = [invalidIdentifier];
  }
  return _handleErrors("mutation", errors);
};

export const validateCndIdentifier = (identifier) => {
  var errors = [];
  if (identifier === null || identifier === undefined || identifier === "") {
    errors = [{ code: "noIdentifierProvided" }];
  }
  if (
    !isValidCndChromosomalLocation(identifier) &&
    !isValidCndGeneSymbol(identifier)
  ) {
    errors = [{ code: "invalidCopyNumberIdentifier" }];
  } else {
    return undefined;
  }
  return _handleErrors("cnd", errors);
};

export const _validateCndSequence = (
  sequence,
  minSequenceLength,
  maxSequenceLength
) => {
  if (sequence === null || sequence === undefined || sequence === "") {
    return [{ code: "noSequenceProvided" }];
  }
  var errors = validateNormalSequenceToken({ seq: sequence, start: 0 });
  var trimmedSequence = toNucleotidesOnly(sequence);
  var sequenceLength = trimmedSequence.length;
  if (sequenceLength < minSequenceLength) {
    errors.push({
      code: "sequenceBelowMinLength",
      length: sequenceLength,
      requiredLength: minSequenceLength,
    });
  }
  if (sequenceLength > maxSequenceLength) {
    errors.push({
      code: "sequenceExceedsMaxLength",
      length: sequenceLength,
      requiredLength: maxSequenceLength,
    });
  }
  return errors;
};

export const _validateGenomeEditDonorSequence = (
  sequence,
  minLength,
  maxLength
) => {
  if (sequence === undefined || sequence === null || sequence === "") {
    return [{ code: "noSequenceProvided" }];
  }
  var errors = [],
    char = "",
    inEditRegion = false,
    editRegionSeen = false,
    nucleotideCount = 0;
  for (var i = 0; i < sequence.length; i++) {
    char = sequence.charAt(i);
    if (WHITESPACE_CHAR.test(char) || DIGIT.test(char)) {
      continue;
    } else if (char === "{") {
      if (editRegionSeen) {
        errors.push({
          code: "unexpectedEditRegionInitiator",
          position: i,
        });
      } else {
        inEditRegion = editRegionSeen = true;
      }
    } else if (char === "}") {
      if (inEditRegion) {
        inEditRegion = false;
      } else {
        errors.push({
          code: "unexpectedEditRegionTerminator",
          position: i,
        });
      }
    } else if (INVALID_NUCLEOTIDE.test(char)) {
      errors.push({
        code: "invalidNucleotide",
        character: char,
        position: i,
      });
    } else {
      nucleotideCount++;
    }
  }
  if (inEditRegion) {
    errors.push({ code: "unterminatedEditRegion", position: i });
  }
  if (nucleotideCount < minLength) {
    errors.push({
      code: "donorBelowMinLength",
      length: nucleotideCount,
      requiredLength: minLength,
    });
  }
  if (nucleotideCount > maxLength) {
    errors.push({
      code: "donorExceedsMaxLength",
      length: nucleotideCount,
      requiredLength: maxLength,
    });
  }
  return errors;
};

export const validateNhejContextSequence = (
  // Context Sequence Validator - Parameters should be passed.
  sequence,
  minBufferLength,
  minSequenceLength,
  maxSequenceLength
) => {
  return _handleErrors(
    "genomeEdit",
    validateContextSequence(
      sequence,
      minBufferLength,
      minSequenceLength,
      maxSequenceLength,
      true
    )
  );
};

export const validateHdrContextSequence = (
  sequence,
  minSequenceLength,
  maxSequenceLength
) => {
  return _handleErrors(
    "genomeEdit",
    validateContextSequence(
      sequence,
      0,
      minSequenceLength,
      maxSequenceLength,
      false
    )
  );
};

export const validGenomes = (applicationType) => {
  switch (applicationType) {
    case "MutationDetection":
      return {
        type: "Mutation",
        genome: GENOMES[0],
      };
    case "GeneExpression":
    case "CopyNumberDetermination":
      return {
        type: "GenomicLocation",
        genome: GENOMES[0],
      };
    case "GenomeEditDetection":
      return {
        type: "NhejGenomeEdit",
      };
    default:
      return null;
  }
};

const _handleErrors = (type, errors) => {
  const errorCount = errors && errors.length;
  if (!errorCount) return;

  var output = new Set();
  errors &&
    errors.forEach((error) => {
      const {
        code,
        location,
        length,
        requiredLength,
        side,
        character,
        position,
        id,
      } = error;
      const errorTemplate = errorText.error[type][code].toString();
      const compiled = _.template(errorTemplate); // String Formatting alternative.
      const tempError = compiled({
        code,
        location,
        length,
        requiredLength,
        side,
        character,
        position,
        id,
      });
      output.add(tempError);
    });

  return output;
};

export const validateMutationSequence = (sequence) => {
  const errors = _validateMutationSequence(
    sequence,
    SEQUENCE_CONFIG.mutation.minSequenceFlankLength,
    SEQUENCE_CONFIG.mutation.maxSequenceLength
  );
  return _handleErrors("mutation", errors);
};

export const validateCellAndGeneTherapy = (sequence) => {
  const errors = _validateCndSequence(
    sequence,
    SEQUENCE_CONFIG.cellAndGeneTherapy.minSequenceLength,
    SEQUENCE_CONFIG.cellAndGeneTherapy.maxSequenceLength
  );
  return _handleErrors("cnd", errors);
};

export const validateCndSequence = (sequence) => {
  const errors = _validateCndSequence(
    sequence,
    SEQUENCE_CONFIG.cnd.minSequenceLength,
    SEQUENCE_CONFIG.cnd.maxSequenceLength
  );
  return _handleErrors("cnd", errors);
};

export const validateGenomeEditDonorSequence = (sequence) => {
  const errors = _validateGenomeEditDonorSequence(
    sequence,
    SEQUENCE_CONFIG.genomeEdit.donor.minSequenceLength,
    SEQUENCE_CONFIG.genomeEdit.donor.maxSequenceLength
  );
  return _handleErrors("genomeEdit", errors);
};

export const configureNhejContextSequenceValidator = (
  genomeIsOther,
  donorSequence,
  sequence
) => {
  return _configureNhejContextSequenceValidator(
    genomeIsOther,
    donorSequence,
    sequence
  );
};

export const configureHdrContextSequenceValidator = (
  genomeIsOther,
  donorSequence,
  sequence
) => {
  return _configureHdrContextSequenceValidator(
    genomeIsOther,
    donorSequence,
    sequence
  );
};

const _configureNhejContextSequenceValidator = (
  genomeIsOther,
  donorSequence,
  sequence
) => {
  var minBufferLength = determineMinBufferLength(genomeIsOther, donorSequence);
  var minSequenceLength = determineMinSequenceLength(
    genomeIsOther,
    donorSequence,
    sequence
  );
  var maxSequenceLength = SEQUENCE_CONFIG.genomeEdit.context.maxSequenceLength;

  return validateNhejContextSequence(
    sequence,
    minBufferLength,
    minSequenceLength,
    maxSequenceLength
  );
};

const _configureHdrContextSequenceValidator = (
  genomeIsOther,
  donorSequence,
  sequence
) => {
  var minSequenceLength = determineMinSequenceLength(
    genomeIsOther,
    donorSequence
  );
  var maxSequenceLength = SEQUENCE_CONFIG.genomeEdit.context.maxSequenceLength;

  return validateHdrContextSequence(
    sequence,
    minSequenceLength,
    maxSequenceLength
  );
};
const determineMinBufferLength = (genomeIsOther, donorSequence) => {
  var minBufferLength = SEQUENCE_CONFIG.genomeEdit.context.minBufferLength;
  if (donorSequence != "") {
    minBufferLength = Math.max(
      minBufferLength,
      SEQUENCE_CONFIG.genomeEdit.context.withDonorMinBufferLength
    );
  }
  if (genomeIsOther) {
    minBufferLength = Math.max(
      minBufferLength,
      SEQUENCE_CONFIG.genomeEdit.context.genomeOtherMinBufferLength
    );
  }
  return minBufferLength;
};

const determineMinSequenceLength = (genomeIsOther, donorSequence) => {
  var minSequenceLength = SEQUENCE_CONFIG.genomeEdit.context.minSequenceLength;
  if (donorSequence != "") {
    var donorSequenceLength = countNucleotideCharacters(donorSequence);
    minSequenceLength = Math.max(
      minSequenceLength,
      donorSequenceLength +
        SEQUENCE_CONFIG.genomeEdit.context.withDonorAdditionalMinSequenceLength
    );
  }
  if (genomeIsOther) {
    minSequenceLength = Math.max(
      minSequenceLength,
      SEQUENCE_CONFIG.genomeEdit.context.genomeOtherMinSequenceLength
    );
  }
  return minSequenceLength;
};

export const validateCommercialID = (assayCommercialId) => {
  let regexString = /d((Hsa|Mmu|Rno)(CP|CNS|MDS|HDS|NHS|MDV|MDM|CPE)|(CP|CNS|MDS|CGTS|EXD))\d{5,11}/gim;
  return (
    !_.isUndefined(assayCommercialId) &&
    !_.isEmpty(assayCommercialId) &&
    assayCommercialId !== "" &&
    regexString.test(assayCommercialId.trim())
  );
};

export const validateCommercialIdArray = (assayCommercialIdString) => {
  let errors = [];
  let commercialIDArray =
    (assayCommercialIdString && assayCommercialIdString.split(",")) || [];
  if (
    commercialIDArray &&
    !_.isEmpty(commercialIDArray) &&
    commercialIDArray.length > 30
  ) {
    errors.push({
      code: "maxLength",
      requiredLength: 30,
    });
    return _handleErrors("bulkUpload", errors);
  }

  commercialIDArray &&
    _.map(commercialIDArray, (commercialId, index) => {
      if (commercialId && commercialId.length < 12) {
        errors.push({
          code: "minLength",
          length: commercialId.length,
          position: index + 1,
          requiredLength: 12,
        });
      } else if (commercialId && !validateCommercialID(commercialId)) {
        errors.push({
          code: "invalidCommercialId",
          id: commercialId,
          position: index + 1,
        });
      }
    });
  return _handleErrors("bulkUpload", errors);
};

export const nullValidationForServerErrors = (
  validationErrorDetails,
  applicationType,
  subType
) => {
  return (
    !_.isEmpty(validationErrorDetails) &&
    !_.isEmpty(validationErrorDetails.AssayDesign) &&
    !_.isUndefined(validationErrorDetails.AssayDesign.ApplicationType) &&
    !!(
      validationErrorDetails.AssayDesign.ApplicationType === applicationType
    ) &&
    !_.isEmpty(validationErrorDetails.AssayDesign.TargetSpecifier) &&
    !_.isUndefined(validationErrorDetails.AssayDesign.TargetSpecifier.Type) &&
    !!(validationErrorDetails.AssayDesign.TargetSpecifier.Type === subType) &&
    !_.isEmpty(validationErrorDetails.Errors)
  );
};

export const isCrawlable = (currentUserAgent) => {
  return CRAWLERS.indexOf(currentUserAgent) > -1;
};
